import { useCallback, useEffect } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';

import StepperCard from '@/components/common/StepperCard.tsx';
import { NavigationBar } from '@/components/NavigationBar';
import { Button } from '@/components/ui/button.tsx';
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store';
import { logger } from '@/lib/logger';
import type { RoutePath } from '@/lib/wizard/wizard';
import { getState } from '@/lib/zipcode.ts';
import { getCarrierTypesNeedingInput } from '@/types/insurance-builder-legacy.ts';
import {
  type InsuranceBuilder,
  setConfirmedInsurances,
  setQueuedInsurance,
  setUnprovidedInsurance,
} from '@/types/insurance-builder.type.ts';
import {
  type QueuedInsurance,
  type UnprovidedInsurance,
  parseCarrierType,
  prettyTypeName,
} from '@/types/insurance.type.ts';
import { InsuranceEditForm } from '@/views/insurance/flows/legacy/components/InsuranceEditForm.tsx';

export default function InsuranceInputConfirm() {
  const navigate = useNavigate();

  const { insuranceZipCode, insuranceBuilder } = useSessionStore();
  const state = getState(insuranceZipCode);

  const { carrierType } = useSearch({
    from: '/appLayout/wizardLayout/insurance/input-confirm',
  });
  const parsedCarrierType = parseCarrierType(carrierType ?? '');
  const carrierInsurance = insuranceBuilder.unverifiedInsurances.find(
    (ins) => ins.carrier.carrierType === parsedCarrierType
  );

  const exitWithoutInsurance = useCallback(() => {
    if (!carrierInsurance) {
      logger.error(
        new Error('No unverified insurance found to confirm. This should never happen.')
      );

      navigate({ to: '/insurance/finalize' });
    }
  }, [carrierInsurance, navigate]);
  useEffect(exitWithoutInsurance, []); // eslint-disable-line react-hooks/exhaustive-deps

  const navigateToNext = useCallback(
    (builder: InsuranceBuilder) => {
      exitWithoutInsurance();
      const missingCarrierTypes = getCarrierTypesNeedingInput(builder);
      if (missingCarrierTypes.length > 0)
        return navigate({
          to: `/insurance/input-confirm?carrierType=${missingCarrierTypes[0]}`,
        });

      if (!builder.ranConfirmation)
        useSessionStore
          .getState()
          .updateState({ insuranceBuilder: { ...builder, ranConfirmation: true } });

      if (builder.queuedInsurances.length > 0)
        return navigate({ to: '/insurance/verify-pending' as RoutePath });

      if (builder.unprovidedInsurances.length > 0) return navigate({ to: '/insurance/follow-up' });

      return navigate({ to: '/insurance/finalize' });
    },
    [exitWithoutInsurance, navigate]
  );

  const enqueueInsurance = useCallback(
    (enqueued: QueuedInsurance) => {
      const updatedBuilder = setQueuedInsurance(insuranceBuilder, enqueued);
      useSessionStore.getState().updateState({ insuranceBuilder: updatedBuilder });
      navigateToNext(updatedBuilder);
    },
    [insuranceBuilder, navigateToNext]
  );

  const confirmInsurance = useCallback(() => {
    const updatedBuilder = setConfirmedInsurances(insuranceBuilder, carrierInsurance!);
    useSessionStore.getState().updateState({ insuranceBuilder: updatedBuilder });
    navigateToNext(updatedBuilder);
  }, [insuranceBuilder, carrierInsurance, navigateToNext]);

  const toFollowUp = useCallback(
    (insurance: UnprovidedInsurance) => {
      const updatedBuilder = setUnprovidedInsurance(insuranceBuilder, insurance);
      useSessionStore.getState().updateState({ insuranceBuilder: updatedBuilder });
      navigateToNext(updatedBuilder);
    },
    [insuranceBuilder, navigateToNext]
  );

  const toInsuranceStart = () => navigate({ to: '/insurance/pre-discovery' });
  const toConfirm = () => navigate({ to: '/insurance/finalize' });

  const nothingToConfirm = !carrierInsurance || !parsedCarrierType;

  return (
    <section>
      <NavigationBar onBack={toInsuranceStart} backLabel="Insurance info" stepIndicator="3 of 4" />
      <StepperCard currentStep={3} totalSteps={4}>
        <CardHeader>
          <CardTitle>
            Our system couldn’t find your{' '}
            {parsedCarrierType ? prettyTypeName(parsedCarrierType) : 'insurance'} plan—please
            confirm your details
          </CardTitle>
        </CardHeader>

        <CardContent>
          {nothingToConfirm ? (
            <div className="space-y-4">
              <p className="text-rose-500">
                Oops there's nothing to confirm. This shouldn't have happened.
              </p>
              <Button className="w-full" onClick={toConfirm}>
                Review Insurances
              </Button>
            </div>
          ) : (
            <InsuranceEditForm
              initialData={{
                state: state ?? '',
                internalId: carrierInsurance.carrier.internalId,
                memberId: carrierInsurance.memberId,
              }}
              carrierType={parsedCarrierType}
              onSubmitChanges={enqueueInsurance}
              onConfirm={confirmInsurance}
              onCannotProvide={toFollowUp}
            />
          )}
        </CardContent>
      </StepperCard>
    </section>
  );
}
